// VollanlageWeiter.tsx — „+ Weitere Informationen erfassen" unter jeder
// Schnellanlage (Plan 2026-10-01, User-Entscheidungen 02.10.2026).
//
// Der Knopf ersetzt nichts: Die Schnellanlage funktioniert weiter wie bisher.
// Ein Klick öffnet die volle Kontaktanlage als Fenster
// (`openContactFormWindow`), vorbelegt mit allem, was in der Schnellanlage
// steht. Ihr Speichern-Knopf heißt „Übernehmen" (bzw. „Weiter" in der
// Mail-Zuweisung): Der Kontakt wird SOFORT angelegt und steht danach im
// Ausgangsformular als BESTEHENDER Kontakt (`contactId`) — beim Speichern des
// Hauptkontakts entsteht nur noch die Beziehung, kein zweiter Kontakt.
//
// Abbrechen (Fenster schließen) lässt die Schnellanlage unverändert.
//
// Dialog-Ebenen: Fenster liegen im Fenster-Layer (z 9000, eigener
// Stacking-Context), Modals darüber (`Z_LAYERS.ueberFenster` und höher). Ein
// aus einem Modal geöffnetes Fenster läge DAHINTER. Das Modal wird deshalb für
// die Dauer der Vollanlage ausgeblendet — nicht geschlossen: Sein Zustand
// bleibt erhalten, und es erscheint wieder, sobald das Fenster zu ist (mit dem
// neuen Kontakt bzw. unverändert). Escape erreicht das ausgeblendete Modal
// währenddessen nicht, sonst schlösse ein Escape in der Vollanlage es still.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../risikoanalyseStep/tokens";
import { useWindowManagerOptional } from "../windowManager/WindowManagerContext";
import { openContactFormWindow } from "../windowManager/ContactFormWindow";
import type { ContactRef } from "../relationsInline/types";
import { refDisplayName } from "../relationsInline/types";
import { hinweisen } from "../redesign/bestaetigen";
import type { Ownership, SharedAddress } from "../relationsInline/persistRelations";
import { fetchLegalFormOptions } from "../../utils/legalFormOptions";
import { apiUrl } from "../../utils/api";
import type { FormState } from "../../utils/formUtils";
import {
  kontaktRefAusFormular,
  ownershipAusKontakt,
  vollanlageVorbereiten,
} from "../../utils/refZuVorbelegung";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/** Woraus heraus angelegt wird — gesetzt von den Anlage-Masken und der Akte. */
export type VollanlageEltern = {
  /** Gespeicherter Elternkontakt (Akte). Fehlt bei einer laufenden Neuanlage. */
  elternId?: number;
  /** Für den Fenstertitel „Neuer Privatkontakt für <Name>". */
  elternName?: string;
  /** FO/BO des Elternkontakts aus seinem Formular. */
  ownership?: Ownership | null;
  /** Wohnanschrift des Elternkontakts (für neue Haushaltsmitglieder). */
  haushaltsAnschrift?: SharedAddress | null;
};

export const VollanlageElternContext = React.createContext<VollanlageEltern | null>(null);

/**
 * Welche Vollanlagen aus den Schnellanlagen eines Formulars gerade offen sind.
 *
 * Solange eine offen ist, steht der Unterkontakt im Formular noch als
 * Schnellanlage (`manual`). Speicherte man jetzt das Formular, legte es ihn
 * über die Schnellanlage an — und „Übernehmen" danach ein zweites Mal. Das
 * Formular (bzw. die Akte) blockt deshalb sein Speichern, bis jede offene
 * Vollanlage übernommen oder abgebrochen ist.
 */
export type OffeneVollanlagen = {
  geoeffnet: (schluessel: string, name: string) => void;
  geschlossen: (schluessel: string) => void;
  /** Namen der Kontakte, deren Vollanlage gerade offen ist. */
  namen: () => string[];
};

export const OffeneVollanlagenContext = React.createContext<OffeneVollanlagen | null>(null);

/** Ein Register offener Vollanlagen (stabil über die Lebensdauer der Komponente). */
export function useOffeneVollanlagenRegister(): OffeneVollanlagen {
  const ref = React.useRef<OffeneVollanlagen | null>(null);
  if (!ref.current) {
    const offen = new Map<string, string>();
    ref.current = {
      geoeffnet: (k, n) => void offen.set(k, n),
      geschlossen: (k) => void offen.delete(k),
      namen: () => [...offen.values()],
    };
  }
  return ref.current;
}

/**
 * Vor dem Speichern eines Formulars: Ist noch eine Vollanlage offen, sagt ein
 * Hinweis, welche, und das Speichern unterbleibt (`true` = blockiert).
 */
export async function speichernWegenVollanlageBlockiert(register: OffeneVollanlagen | null | undefined): Promise<boolean> {
  const namen = register?.namen() ?? [];
  if (!namen.length) return false;
  const liste = namen.map((n) => `„${n}"`).join(", ");
  await hinweisen({
    titel: "Anlage noch geöffnet",
    text:
      `Bitte zuerst die geöffnete Anlage von ${liste} abschließen oder abbrechen.\n\n` +
      "Sonst würde der Kontakt hier und dort ein zweites Mal angelegt.",
  });
  return true;
}

export type VollanlageErgebnis = {
  contactId: number;
  /** Der neue Kontakt als bestehender Ref (`contactId`, kein `manual`). */
  ref: ContactRef;
  /** Gespeicherter Formularstand der Vollanlage. */
  felder?: FormState;
};

export type VollanlageAuftrag = {
  /** Stand der Schnellanlage. */
  kontakt: ContactRef;
  /** Neuer Kontakt kommt in den Haushalt des Elternkontakts. */
  imHaushalt?: boolean;
  /** Verein: Rechtsform e.V., falls sonst keine erkennbar ist. */
  verein?: boolean;
  /** Knopftext der Vollanlage. Standard „Übernehmen". */
  speichernText?: string;
  /** Überschreibt einzelne Angaben des Kontexts. */
  eltern?: VollanlageEltern;
  /**
   * Weitere Formularfelder, die der ContactRef nicht kennt (Status, Folgefelder
   * wie `rel_household`). Gewinnen gegen die abgeleiteten Werte.
   */
  zusatzFelder?: Record<string, string>;
  onCreated: (e: VollanlageErgebnis) => void;
  onAbbruch?: () => void;
  /** Das Fenster ist offen (z. B. um Knöpfe der Schnellanlage zu sperren). */
  onGeoeffnet?: () => void;
};

/** Standard-Beschriftung des Speichern-Knopfs der Vollanlage. */
export const VOLLANLAGE_UEBERNEHMEN = "Übernehmen";

/**
 * Das Modal, in dem der Auslöser steht — `null`, wenn er auf der Seite oder in
 * einem Fenster steht (dann liegt die Vollanlage ohnehin obenauf).
 */
export function ebeneFinden(el: Element | null | undefined): HTMLElement | null {
  if (!el) return null;
  const markiert = el.closest<HTMLElement>("[data-vollanlage-ebene], .kvp-rd-scrim");
  if (markiert) return markiert;
  const dialog = el.closest<HTMLElement>('[aria-modal="true"]');
  if (!dialog) return null;
  // Portalierte Dialoge (Fluent u. a.) samt Hintergrund: ihr Wurzelknoten
  // direkt unter <body>. Die App-Wurzel selbst wird nie ausgeblendet.
  let knoten: HTMLElement = dialog;
  while (knoten.parentElement && knoten.parentElement !== document.body) knoten = knoten.parentElement;
  return knoten.parentElement === document.body && knoten.id !== "root" ? knoten : dialog;
}

/**
 * Blendet das Modal des Auslösers aus und liefert die Rücknahme. Ohne Modal
 * passiert nichts.
 */
export function ebeneAusblenden(ausloeser: Element | null | undefined): () => void {
  const ebene = ebeneFinden(ausloeser);
  if (!ebene) return () => undefined;
  const vorher = ebene.style.display;
  ebene.style.display = "none";
  ebene.setAttribute("data-vollanlage-ausgeblendet", "");
  // Bubble-Phase am document: Elemente der Vollanlage bekommen Escape noch
  // (React lauscht an der App-Wurzel, darunter), die window-Listener der
  // Modals nicht mehr.
  const escapeAbfangen = (e: KeyboardEvent) => {
    if (e.key === "Escape") e.stopPropagation();
  };
  alleDokumente.addEventListener("keydown", escapeAbfangen);
  let erledigt = false;
  return () => {
    if (erledigt) return;
    erledigt = true;
    alleDokumente.removeEventListener("keydown", escapeAbfangen);
    ebene.style.display = vorher;
    ebene.removeAttribute("data-vollanlage-ausgeblendet");
  };
}

/** FO/BO eines gespeicherten Elternkontakts — ohne Leserecht: keine. */
async function elternVerantwortungLaden(id: number): Promise<Ownership | null> {
  try {
    const res = await fetch(apiUrl(`contact/${id}`));
    if (!res.ok) return null;
    return ownershipAusKontakt(await res.json());
  } catch {
    return null;
  }
}

const hatInhalt = (o: Ownership | null | undefined): o is Ownership => !!o && Object.keys(o).length > 0;

/**
 * Öffnet die Vollanlage und meldet das Ergebnis. `offen` ist wahr, solange
 * das Fenster existiert; ein zweiter Klick holt es nur nach vorn (kein
 * zweites Fenster, keine zweite Anlage).
 */
export function useVollanlageWeiter() {
  const wm = useWindowManagerOptional();
  const kontext = React.useContext(VollanlageElternContext);
  const register = React.useContext(OffeneVollanlagenContext);
  const [fensterId, setFensterId] = React.useState<string | null>(null);
  const fensterIdRef = React.useRef<string | null>(null);
  const laedtRef = React.useRef(false);
  const laufRef = React.useRef<{ erledigt: boolean; abbruch?: () => void; zurueck: () => void } | null>(null);

  const fensterOffen = wm && fensterId ? wm.windows.some((w) => w.id === fensterId) : false;

  // Fenster weg, ohne dass angelegt wurde → abgebrochen.
  React.useEffect(() => {
    if (!fensterId || !wm || fensterOffen) return;
    const lauf = laufRef.current;
    laufRef.current = null;
    fensterIdRef.current = null;
    setFensterId(null);
    lauf?.zurueck();
    if (lauf && !lauf.erledigt) lauf.abbruch?.();
  }, [fensterId, fensterOffen, wm]);

  // Verschwindet der Auslöser, darf das Modal nicht ausgeblendet bleiben.
  React.useEffect(() => () => laufRef.current?.zurueck(), []);

  const oeffnen = React.useCallback(
    async (auftrag: VollanlageAuftrag, ausloeser?: Element | null): Promise<void> => {
      if (!wm) return;
      const offenesFenster = fensterIdRef.current;
      if (offenesFenster && wm.windows.some((w) => w.id === offenesFenster)) {
        wm.focusWindow(offenesFenster);
        return;
      }
      if (laedtRef.current) return;
      laedtRef.current = true;
      try {
        // Nur gesetzte Angaben des Auftrags überschreiben den Kontext.
        const eltern: VollanlageEltern = { ...(kontext ?? {}) };
        for (const [k, v] of Object.entries(auftrag.eltern ?? {})) {
          if (v !== undefined && v !== null) (eltern as Record<string, unknown>)[k] = v;
        }
        const rechtsformOptionen =
          auftrag.kontakt.type === "Firma" ? await fetchLegalFormOptions().catch(() => []) : [];
        let ownership = hatInhalt(eltern.ownership) ? eltern.ownership : null;
        if (!ownership && eltern.elternId) ownership = await elternVerantwortungLaden(eltern.elternId);
        const v = vollanlageVorbereiten({
          ref: auftrag.kontakt,
          rechtsformOptionen,
          verein: auftrag.verein,
          imHaushalt: auftrag.imHaushalt,
          elternId: eltern.elternId,
          ownership,
          haushaltsAnschrift: eltern.haushaltsAnschrift,
        });
        // Beim Elternformular als offen melden — bis übernommen, abgebrochen
        // oder der Auslöser verschwunden ist (alles über `zurueck`).
        const schluessel = `vollanlage-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        register?.geoeffnet(schluessel, refDisplayName(auftrag.kontakt));
        const ebeneZurueck = ebeneAusblenden(ausloeser);
        const lauf = {
          erledigt: false,
          abbruch: auftrag.onAbbruch,
          zurueck: () => {
            register?.geschlossen(schluessel);
            ebeneZurueck();
          },
        };
        laufRef.current = lauf;
        const id = openContactFormWindow(wm.openWindow, {
          kind: v.kind,
          initialValues: { ...v.initialValues, ...(auftrag.zusatzFelder ?? {}) },
          speichernText: auftrag.speichernText ?? VOLLANLAGE_UEBERNEHMEN,
          zusatzPayload: v.zusatzPayload,
          fuer: eltern.elternName,
          onCreated: (contactId: number, felder?: FormState) => {
            // Genau einmal — das Fenster schließt sich selbst.
            if (lauf.erledigt) return;
            lauf.erledigt = true;
            lauf.zurueck();
            auftrag.onCreated({ contactId, ref: kontaktRefAusFormular(v.kind, contactId, felder), felder });
          },
        });
        fensterIdRef.current = id;
        setFensterId(id);
        auftrag.onGeoeffnet?.();
      } finally {
        laedtRef.current = false;
      }
    },
    [wm, kontext, register],
  );

  return { verfuegbar: !!wm, offen: fensterOffen, oeffnen };
}

const useStyles = makeStyles({
  // Dieselbe Optik wie „+ Kontakt ist noch nicht im System — neu eintippen"
  // (relationsInline/styles.ts `newLink`).
  link: {
    ...shorthands.border("0"),
    backgroundColor: "transparent",
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.86rem",
    cursor: "pointer",
    alignSelf: "flex-start",
    textAlign: "left",
    ...shorthands.padding("2px", "0"),
    ":hover": { textDecorationLine: "underline" },
    ":disabled": { opacity: 0.55, cursor: "default", textDecorationLine: "none" },
  },
});

export type VollanlageWeiterKnopfProps = Omit<VollanlageAuftrag, "kontakt"> & {
  /** Stand der Schnellanlage — als Funktion, wenn er erst beim Klick feststeht. */
  kontakt: ContactRef | (() => ContactRef);
  /** Ohne Fenster-System (z. B. eigenständige Seite): Ausweichweg per Route. */
  ohneFenster?: () => void;
  disabled?: boolean;
  className?: string;
};

/**
 * Der Text-Link „+ Weitere Informationen erfassen". Ohne Fenster-System und
 * ohne Ausweichweg erscheint er nicht — ein Wechsel per Route verwürfe das
 * Ausgangsformular.
 */
export function VollanlageWeiterKnopf(props: VollanlageWeiterKnopfProps) {
  const s = useStyles();
  const { verfuegbar, offen, oeffnen } = useVollanlageWeiter();
  const knopfRef = React.useRef<HTMLButtonElement>(null);
  // Immer die AKTUELLEN Rückrufe: Während die Vollanlage offen ist, kann sich
  // das Ausgangsformular ändern — ein beim Klick festgehaltener Rückruf
  // schriebe dessen alten Stand zurück.
  const aktuell = React.useRef(props);
  aktuell.current = props;

  if (!verfuegbar && !props.ohneFenster) return null;

  return (
    <button
      ref={knopfRef}
      type="button"
      className={props.className ?? s.link}
      data-testid="vollanlage-weiter"
      disabled={props.disabled}
      title={
        offen
          ? "Die vollständige Anlage ist bereits geöffnet"
          : "In der vollständigen Kontaktanlage weiter erfassen — die Angaben von hier werden mitgenommen. Der Kontakt wird dort beim Übernehmen sofort angelegt."
      }
      onClick={() => {
        const p = aktuell.current;
        if (!verfuegbar) {
          p.ohneFenster?.();
          return;
        }
        void oeffnen(
          {
            kontakt: typeof p.kontakt === "function" ? p.kontakt() : p.kontakt,
            imHaushalt: p.imHaushalt,
            verein: p.verein,
            speichernText: p.speichernText,
            eltern: p.eltern,
            zusatzFelder: p.zusatzFelder,
            onCreated: (e) => aktuell.current.onCreated(e),
            onAbbruch: () => aktuell.current.onAbbruch?.(),
            onGeoeffnet: () => aktuell.current.onGeoeffnet?.(),
          },
          knopfRef.current,
        );
      }}
    >
      + Weitere Informationen erfassen
    </button>
  );
}
